import { z } from 'zod';
import { savedDeckDisplaySchema } from './deck-display.mjs';
import { tableColumnSchema, tableRowSchema, tablePayloadSchema } from './models/native.mjs';
import { boundedSchema, id, text, unique } from './models/common.mjs';
import { workbookSheetSchema, workbookPayloadSchema } from './models/workbook.mjs';
import { scalarSchema } from './models/value.mjs';
import { nativePresentationFields, BOX_SPACING } from './models/presentation.mjs';
import { ManagedImageProjection } from './images/projection.mjs';
import { ImageFit, ImageRatio } from './images/values.mjs';

// Browser display values contain no source binding, evidence or object handles.
const marks = z.array(z.enum(['bold', 'italic', 'underline'])).max(3);
export const savedDisplayRunSchema = z.discriminatedUnion('kind', [
  z
    .object({
      id,
      kind: z.literal('text'),
      text: text(),
      marks,
      ...nativePresentationFields({ colours: ['text'] }),
    })
    .strict(),
  z
    .object({
      id,
      kind: z.literal('value'),
      value: scalarSchema,
      marks,
      ...nativePresentationFields({ colours: ['text'], numeric: true }),
    })
    .strict(),
]);
const runs = z.array(savedDisplayRunSchema).max(500);
const presentation = nativePresentationFields({
  colours: ['text', 'background', 'border'],
  spacing: BOX_SPACING,
});
export const savedDisplayBlockSchema = z.discriminatedUnion('kind', [
  z
    .object({
      id,
      kind: z.literal('image'),
      ref: ManagedImageProjection,
      ratio: ImageRatio,
      fit: ImageFit,
      cropSafe: z.boolean(),
      caption: text(1000).optional(),
      ...presentation,
    })
    .strict()
    .refine((image) => image.fit !== 'cover' || image.cropSafe, 'image_cover_not_crop_safe'),
  z.object({ id, kind: z.literal('paragraph'), runs, ...presentation }).strict(),
  z
    .object({
      id,
      kind: z.literal('heading'),
      level: z.union([z.literal(1), z.literal(2), z.literal(3)]),
      runs,
      ...presentation,
    })
    .strict(),
  z
    .object({
      id,
      kind: z.literal('list'),
      ordered: z.boolean(),
      items: z.array(z.object({ id, runs }).strict()).max(500),
      ...presentation,
    })
    .strict(),
  z
    .object({
      id,
      kind: z.literal('table'),
      registeredPayloadRef: id,
      display: z.lazy(() => savedTableDisplaySchema),
    })
    .strict()
    .refine((block) => block.registeredPayloadRef === block.display.id),
  z
    .object({
      id,
      kind: z.literal('chart'),
      registeredPayloadRef: id,
      display: z.lazy(() => savedChartDisplaySchema),
    })
    .strict()
    .refine((block) => block.registeredPayloadRef === block.display.id),
]);
const blocks = z.array(savedDisplayBlockSchema).max(2000);
// The App page facts (PUB's projectAppPageFacts, six fields) and nothing else: no logical App id, no manifest,
// no source binding. The saved envelope around it is the same for every kind.
const version = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);
const appPageFacts = z
  .object({
    kind: z.literal('app'),
    app: z.object({ name: text(300), description: text(), ownerName: text(300) }).strict(),
    audience: z
      .object({ team: text(300) })
      .strict()
      .nullable(),
    savedVersion: version,
    publishedVersion: version.nullable(),
    draftVersion: version.nullable(),
    rights: z
      .object({
        role: z.enum(['user', 'editor', 'owner']),
        canPublish: z.boolean(),
        canShare: z.boolean(),
        canUnshare: z.boolean(),
      })
      .strict(),
    failure: z
      .object({
        version,
        people: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
        /** Canonical ISO instant; the saved-display consumer formats it once. */
        time: z.string().datetime({ offset: true }),
        previousVersion: version.nullable(),
      })
      .strict()
      .nullable(),
  })
  .strict();
function validLiteralTable(value) {
  const canonical = tablePayloadSchema.safeParse({
    schemaVersion: 1,
    id: value.id,
    title: value.title,
    columns: value.columns,
    rows: value.rows,
  });
  return (
    canonical.success &&
    value.rows.every((row) => Object.values(row.cells).every((slot) => slot.kind === 'literal'))
  );
}
export const savedTableDisplaySchema = z
  .object({
    kind: z.literal('table'),
    id,
    title: text(300),
    columns: z.array(tableColumnSchema).min(1).max(100),
    rows: z.array(tableRowSchema),
  })
  .strict()
  .refine(validLiteralTable, { message: 'Saved Table display requires canonical literal cells' });

export const savedChartDisplaySchema = z
  .object({
    kind: z.literal('chart'),
    id,
    title: text(300),
    chartType: z.enum(['bar', 'line', 'column', 'stacked-column']),
    tableRef: id,
    xColumnId: id,
    yColumnIds: z.array(id).min(1).max(12),
    table: savedTableDisplaySchema,
  })
  .strict()
  .superRefine((value, check) => {
    unique([value.xColumnId, ...value.yColumnIds], check, ['yColumnIds']);
    const columns = new Map(value.table.columns.map((column) => [column.id, column]));
    if (
      value.tableRef !== value.table.id ||
      !columns.has(value.xColumnId) ||
      value.yColumnIds.some(
        (id) =>
          !['integer', 'decimal'].includes(columns.get(id)?.type) ||
          value.table.rows.some((row) => !['decimal', 'null'].includes(row.cells[id]?.value.kind)),
      )
    )
      check.addIssue({
        code: 'custom',
        message: 'Chart axes must belong to the admitted literal Table',
      });
  });
const display = z
  .discriminatedUnion('kind', [
    appPageFacts,
    z.object({ kind: z.literal('document'), id, title: text(300), blocks }).strict(),
    savedTableDisplaySchema,
    savedChartDisplaySchema,
    z
      .object({
        kind: z.literal('spreadsheet'),
        id,
        title: text(300),
        sheets: z.array(workbookSheetSchema).min(1).max(32),
      })
      .strict(),

    z
      .object({
        kind: z.literal('report'),
        id,
        title: text(300),
        sections: z
          .array(z.object({ id, title: text(300).optional(), blocks }).strict())
          .min(1)
          .max(200),
      })
      .strict(),
  ])
  .superRefine((value, ctx) => {
    if (value.kind === 'app') return; // no node ids to keep unique
    if (value.kind === 'chart' || value.kind === 'table') return;
    if (value.kind === 'spreadsheet') {
      const workbook = workbookPayloadSchema.safeParse({
        schemaVersion: 1,
        id: value.id,
        title: value.title,
        sheets: value.sheets,
      });
      if (
        !workbook.success ||
        value.sheets.some((sheet) =>
          sheet.cells.some((cell) => cell.kind !== 'value' || cell.slot.kind !== 'literal'),
        )
      )
        ctx.addIssue({
          code: 'custom',
          message: 'Saved Spreadsheet display requires canonical literal cells',
        });
      return;
    }
    const sections = value.kind === 'report' ? value.sections : [];
    const allBlocks = value.kind === 'report' ? sections.flatMap((s) => s.blocks) : value.blocks;
    unique(
      [
        value.id,
        ...sections.map((s) => s.id),
        ...allBlocks.flatMap((block) => [
          block.id,
          ...(block.runs ?? []).map((run) => run.id),
          ...(block.items ?? []).flatMap((item) => [item.id, ...item.runs.map((run) => run.id)]),
        ]),
      ],
      ctx,
      ['display'],
    );
  });
export const savedArtifactDisplaySchema = boundedSchema(z.union([display, savedDeckDisplaySchema]));
/** What this saved version was built with (registry id and its canonical display label, projected
 * by the server from THIS version's own record) and who can see it NOW; every part is a fact from
 * the version's record and the current authority, `null` when it is not proven (never inferred). */
export const savedArtifactProvenanceSchema = z
  .object({
    model: z
      .object({ registryId: id, displayLabel: text(80) })
      .strict()
      .nullable(),
    audience: z.enum(['private', 'shared']).nullable(),
  })
  .strict();
export const savedArtifactViewSchema = boundedSchema(
  z
    .object({
      schemaVersion: z.literal(1),
      versionId: id,
      artifactId: id,
      revisionId: id,
      number: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
      purpose: z.enum(['viewer', 'editor', 'source']),
      display: savedArtifactDisplaySchema,
      provenance: savedArtifactProvenanceSchema.optional(),
    })
    .strict(),
);
